import './Arbitrage.css'
import {
  Typography,
  Box,
  Grid,
  Tooltip,
  styled,
  TextField,
  CircularProgress,
  Link,
  Button,
} from '@mui/material'
import { u8, struct, nu64 } from '@solana/buffer-layout'
import { makeStyles } from '@material-ui/core'
import { FC, useCallback, useState } from 'react'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { ArbToken } from '../../types'
import { ArbBananasEstimate } from './ArbBananasEstimate'
import { ArbKongEstimate } from './ArbKongEstimate'
import { useDispatch, useSelector } from 'react-redux'
import {
  getArbTransactionSignature,
  setArbTransactionSignature,
} from '../../redux/modules/app'
import LaunchIcon from '@material-ui/icons/Launch'
import { WALLET_ADAPTER_NETWORK } from '../../utils/wallet-config'
import {
  LAMPORTS_PER_SOL,
  PublicKey,
  Signer,
  Transaction,
  TransactionInstruction,
  TransactionSignature,
} from '@solana/web3.js'
import { TOKEN_PROGRAM_ID } from '@solana/spl-token'
import {
  BANANA_MINT,
  BANANA_MINT_AUTHORITY_SEED,
  KONG_MINT,
  KONG_MINT_AUTHORITY_SEED,
  KONG_ORACLE_PRICE,
  KONG_ORACLE_PRODUCT,
  PROGRAM_STATE_ACCOUNT,
  RAD_ID,
  RENT_PROGRAM_ID,
  SPL_ASSOCIATED_TOKEN_ACCOUNT_PROGRAM_ID,
  SYSTEM_PROGRAM_ID,
} from '../../utils/constants'
import { useNotify } from '../../utils/notify'
import { useConnection, useWallet } from '@solana/wallet-adapter-react'
import {
  getCollateralAsNumber,
  validNumericTrailingCharCheck,
} from '../../utils/helpers'

export const CssTextField = styled(TextField)({
  '& label.MuiInputLabel': {
    color: 'white',
  },
  '& label.MuiInputLabel-root': {
    color: 'white',
  },
  '& label.Mui-focused': {
    color: 'white',
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: 'green',
  },
  '& .MuiInputBase-root': {
    color: 'white',
    backgroundColor: 'transparent',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'green',
    },
    '&:hover fieldset': {
      borderColor: 'white',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'green',
    },
  },
})

export const useStyles = makeStyles({
  link: {
    color: '#ffffff',
    display: 'flex',
    alignItems: 'right',
    textDecoration: 'underline',
    '&:hover': {
      color: '#000000',
    },
  },
  icon: {
    fontSize: 20,
  },
})

async function findAssociatedTokenAddress(
  walletAddress: PublicKey,
  tokenMintAddress: PublicKey
): Promise<PublicKey> {
  return (
    await PublicKey.findProgramAddress(
      [
        walletAddress.toBuffer(),
        TOKEN_PROGRAM_ID.toBuffer(),
        tokenMintAddress.toBuffer(),
      ],
      SPL_ASSOCIATED_TOKEN_ACCOUNT_PROGRAM_ID
    )
  )[0]
}

export const Arbitrage: FC = () => {
  const { connection } = useConnection()
  const { publicKey, sendTransaction } = useWallet()
  const [loading, setLoading] = useState(false)
  const [arbToken, setArbToken] = useState<ArbToken>('BANANA')
  const [amount, setAmount] = useState<string>('')
  const dispatch = useDispatch()
  const arbTransactionSignature = useSelector(getArbTransactionSignature)
  const styles = useStyles()
  const notify = useNotify()

  function handleSetAmount(ev: React.ChangeEvent<HTMLInputElement>) {
    if (validNumericTrailingCharCheck(ev.target.value)) {
      setAmount(validNumericTrailingCharCheck(ev.target.value))
    } else if (ev.target.value === '') {
      setAmount('')
    }
  }

  const changeArbDirection = useCallback(() => {
    setAmount('')
    if (arbToken === 'BANANA') {
      setArbToken('KONG')
    } else {
      setArbToken('BANANA')
    }
  }, [arbToken])

  const arbitrageBananas = useCallback(async () => {
    if (!publicKey) {
      notify('error', 'Wallet not connected!')
      return
    }
    setLoading(true)

    let signature: TransactionSignature = ''
    try {
      let dataLayout = struct([u8('instruction'), nu64('lamports')])
      const data = Buffer.alloc(dataLayout.span)
      dataLayout.encode(
        {
          instruction: 3,
          lamports: getCollateralAsNumber(amount) * LAMPORTS_PER_SOL,
        },
        data
      )

      const kongATA = await findAssociatedTokenAddress(publicKey, KONG_MINT)
      const kongMintAuthority = await PublicKey.findProgramAddress(
        [
          PROGRAM_STATE_ACCOUNT.toBuffer(),
          Buffer.from(KONG_MINT_AUTHORITY_SEED),
        ],
        RAD_ID
      )

      const bananaATA = await findAssociatedTokenAddress(publicKey, BANANA_MINT)

      const transactionItems = []

      // check if ataAccountExists
      const ataAccountInfo = await connection.getAccountInfo(
        kongATA,
        'finalized'
      )

      if (!ataAccountInfo) {
        let dataLayoutATA = struct([u8('instruction')])
        const ataData = Buffer.alloc(dataLayoutATA.span)
        dataLayoutATA.encode(
          {
            instruction: 0,
          },
          ataData
        )
        transactionItems.push(
          new TransactionInstruction({
            keys: [
              { pubkey: publicKey, isSigner: false, isWritable: true },
              { pubkey: kongATA, isSigner: false, isWritable: true },
              { pubkey: publicKey, isSigner: false, isWritable: false },
              {
                pubkey: KONG_MINT,
                isSigner: false,
                isWritable: false,
              },
              { pubkey: SYSTEM_PROGRAM_ID, isSigner: false, isWritable: false },
              { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
              { pubkey: RENT_PROGRAM_ID, isSigner: false, isWritable: false },
            ],
            programId: SPL_ASSOCIATED_TOKEN_ACCOUNT_PROGRAM_ID,
            data: ataData,
          })
        )
      }

      transactionItems.push(
        new TransactionInstruction({
          keys: [
            {
              pubkey: PROGRAM_STATE_ACCOUNT,
              isSigner: false,
              isWritable: false,
            },
            { pubkey: bananaATA, isSigner: false, isWritable: true },
            { pubkey: kongATA, isSigner: false, isWritable: true },
            { pubkey: BANANA_MINT, isSigner: false, isWritable: true },
            {
              pubkey: publicKey,
              isSigner: true,
              isWritable: false,
            },
            { pubkey: KONG_MINT, isSigner: false, isWritable: true },
            {
              pubkey: kongMintAuthority[0],
              isSigner: false,
              isWritable: false,
            },
            { pubkey: KONG_ORACLE_PRODUCT, isSigner: false, isWritable: false },
            { pubkey: KONG_ORACLE_PRICE, isSigner: false, isWritable: false },
            { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
          ],
          programId: RAD_ID,
          data,
        })
      )

      const walletBalance = await connection.getTokenAccountBalance(bananaATA)
      const walletBalanceBananas = walletBalance.value.uiAmount
      if (
        walletBalanceBananas !== null &&
        walletBalanceBananas < getCollateralAsNumber(amount)
      ) {
        throw Error(
          `Insufficient wallet balance of ${walletBalanceBananas.toFixed(
            5
          )} Bananas for transaction of ${amount} Bananas.`
        )
      }

      notify('info', 'Burn Request Approval Needed')
      const signers: Signer[] = []

      signature = await sendTransaction(
        new Transaction().add(...transactionItems),
        connection,
        {
          signers: signers,
        }
      )
      notify('info', 'Transaction sent:', signature)
      dispatch(setArbTransactionSignature(signature))

      await connection.confirmTransaction(signature, 'processed')
      notify('success', 'Transaction successful!', signature)
      setLoading(false)
      setAmount('')
    } catch (error: any) {
      setLoading(false)
      dispatch(setArbTransactionSignature())
      notify('error', `Transaction failed! ${error?.message}`, signature)
      return
    }
  }, [dispatch, amount, connection, notify, publicKey, sendTransaction])

  const arbitrageKong = useCallback(async () => {
    if (!publicKey) {
      notify('error', 'Wallet not connected!')
      return
    }
    setLoading(true)

    let signature: TransactionSignature = ''
    try {
      let dataLayout = struct([u8('instruction'), nu64('lamports')])
      const data = Buffer.alloc(dataLayout.span)
      dataLayout.encode(
        {
          instruction: 2,
          lamports: getCollateralAsNumber(amount) * LAMPORTS_PER_SOL,
        },
        data
      )

      const kongATA = await findAssociatedTokenAddress(publicKey, KONG_MINT)

      const bananaATA = await findAssociatedTokenAddress(publicKey, BANANA_MINT)
      const bananaMintAuthority = await PublicKey.findProgramAddress(
        [
          PROGRAM_STATE_ACCOUNT.toBuffer(),
          Buffer.from(BANANA_MINT_AUTHORITY_SEED),
        ],
        RAD_ID
      )

      const transactionItems = []

      // check if ataAccountExists
      const ataAccountInfo = await connection.getAccountInfo(
        bananaATA,
        'finalized'
      )

      if (!ataAccountInfo) {
        let dataLayoutATA = struct([u8('instruction')])
        const ataData = Buffer.alloc(dataLayoutATA.span)
        dataLayoutATA.encode(
          {
            instruction: 0,
          },
          ataData
        )
        transactionItems.push(
          new TransactionInstruction({
            keys: [
              { pubkey: publicKey, isSigner: false, isWritable: true },
              { pubkey: bananaATA, isSigner: false, isWritable: true },
              { pubkey: publicKey, isSigner: false, isWritable: false },
              {
                pubkey: BANANA_MINT,
                isSigner: false,
                isWritable: false,
              },
              { pubkey: SYSTEM_PROGRAM_ID, isSigner: false, isWritable: false },
              { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
              { pubkey: RENT_PROGRAM_ID, isSigner: false, isWritable: false },
            ],
            programId: SPL_ASSOCIATED_TOKEN_ACCOUNT_PROGRAM_ID,
            data: ataData,
          })
        )
      }

      transactionItems.push(
        new TransactionInstruction({
          keys: [
            {
              pubkey: PROGRAM_STATE_ACCOUNT,
              isSigner: false,
              isWritable: false,
            },
            { pubkey: kongATA, isSigner: false, isWritable: true },
            { pubkey: bananaATA, isSigner: false, isWritable: true },
            { pubkey: KONG_MINT, isSigner: false, isWritable: true },
            {
              pubkey: publicKey,
              isSigner: true,
              isWritable: false,
            },
            { pubkey: BANANA_MINT, isSigner: false, isWritable: true },
            {
              pubkey: bananaMintAuthority[0],
              isSigner: false,
              isWritable: false,
            },
            { pubkey: KONG_ORACLE_PRODUCT, isSigner: false, isWritable: false },
            { pubkey: KONG_ORACLE_PRICE, isSigner: false, isWritable: false },
            { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
          ],
          programId: RAD_ID,
          data,
        })
      )

      const walletBalance = await connection.getTokenAccountBalance(kongATA)
      const walletBalanceKONG = walletBalance.value.uiAmount
      if (
        walletBalanceKONG !== null &&
        walletBalanceKONG < getCollateralAsNumber(amount)
      ) {
        throw Error(
          `Insufficient wallet balance of ${walletBalanceKONG.toFixed(
            5
          )} Kong for transaction of ${amount} Kong.`
        )
      }

      notify('info', 'Burn Request Approval Needed')
      const signers: Signer[] = []

      signature = await sendTransaction(
        new Transaction().add(...transactionItems),
        connection,
        {
          signers: signers,
        }
      )
      notify('info', 'Transaction sent:', signature)
      dispatch(setArbTransactionSignature(signature))

      await connection.confirmTransaction(signature, 'processed')
      notify('success', 'Transaction successful!', signature)
      setLoading(false)
      setAmount('')
    } catch (error: any) {
      setLoading(false)
      dispatch(setArbTransactionSignature())
      notify('error', `Transaction failed! ${error?.message}`, signature)
      return
    }
  }, [dispatch, amount, connection, notify, publicKey, sendTransaction])

  const maxBananas = useCallback(async () => {
    if (!publicKey) {
      notify('error', 'Wallet not connected!')
      return
    }

    try {
      const bananaATA = await findAssociatedTokenAddress(publicKey, BANANA_MINT)
      const walletBalance = await connection.getTokenAccountBalance(bananaATA)
      const walletBalanceBananas = walletBalance.value.uiAmount
      walletBalanceBananas !== null &&
        setAmount(walletBalanceBananas.toString())

      notify('success', 'Set Bananas to MAX of: ' + walletBalanceBananas)
    } catch (error: any) {
      notify('error', `Failed to fetch Banana balance ${error?.message}`)
      return
    }
  }, [notify, connection, publicKey])

  const maxKong = useCallback(async () => {
    if (!publicKey) {
      notify('error', 'Wallet not connected!')
      return
    }

    try {
      const kongATA = await findAssociatedTokenAddress(publicKey, KONG_MINT)
      const walletBalance = await connection.getTokenAccountBalance(kongATA)
      const walletBalanceKong = walletBalance.value.uiAmount
      walletBalanceKong !== null && setAmount(walletBalanceKong.toString())

      notify('success', 'Set Kong to MAX of: ' + walletBalanceKong)
    } catch (error: any) {
      notify('error', `Failed to fetch Banana balance ${error?.message}`)
      return
    }
  }, [notify, connection, publicKey])

  if (arbToken === 'BANANA') {
    return (
      <div className="Arbitrage-outter-container">
        <div className="Arbitrage-container">
          <Typography
            id="modal-modal-title"
            variant="h6"
            component="h2"
            align={'center'}
            color={'white'}
          >
            BANANA / KONG
          </Typography>
          <br />

          <div className="Div-relative-container  ">
            <CssTextField
              variant="outlined"
              autoFocus={true}
              fullWidth={true}
              type={'text'}
              value={amount}
              onChange={handleSetAmount.bind(this)}
              label="Bananas to Burn for Kong"
              id="banana-amount"
            />

            <Button
              variant={'outlined'}
              color={'success'}
              className="Input-max-button"
              onClick={maxBananas}
            >
              max
            </Button>
          </div>
          <br />
          <br />
          <Button
            variant={'contained'}
            color={'warning'}
            onClick={changeArbDirection}
            className="Arb-change-direction-button"
          >
            Change Burn Direction
          </Button>
          <br />
          <br />
          <ArbBananasEstimate amount={amount} />
          <br />
          <br />
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={10}>
                <Typography
                  id="modal-modal-title"
                  variant="caption"
                  component="h2"
                  align={'left'}
                  color={'white'}
                >
                  Slippage Tolerance
                  <Tooltip
                    title="The maximum difference between your estimated price and execution price."
                    placement="right"
                  >
                    <HelpOutlineIcon
                      style={{
                        position: 'relative',
                        marginLeft: '2px',
                        top: '5px',
                      }}
                      fontSize="small"
                      color={'primary'}
                    />
                  </Tooltip>
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography
                  id="modal-modal-title"
                  variant="caption"
                  component="h2"
                  align={'right'}
                  color={'white'}
                  style={{ position: 'relative', top: '5px' }}
                >
                  0.5%
                </Typography>
              </Grid>
            </Grid>
          </Box>
          <br />
          {loading ? (
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={7}>
                  <CircularProgress color="success" />
                </Grid>
                <Grid item xs={5}>
                  {arbTransactionSignature && (
                    <Link
                      className={styles.link}
                      href={`https://explorer.solana.com/tx/${arbTransactionSignature}?cluster=${WALLET_ADAPTER_NETWORK}`}
                      target="_blank"
                      align="right"
                    >
                      Transaction
                      <LaunchIcon className={styles.icon} />
                    </Link>
                  )}
                </Grid>
              </Grid>
            </Box>
          ) : (
            <>
              <Button onClick={arbitrageBananas} variant={'contained'}>
                Burn Bananas for Kong
              </Button>
            </>
          )}
          <br />
          <br />
        </div>
      </div>
    )
  } else {
    return (
      <div className="Arbitrage-outter-container">
        <div className="Arbitrage-container">
          <Typography
            id="modal-modal-title"
            variant="h6"
            component="h2"
            align={'center'}
            color={'white'}
          >
            KONG / BANANA
          </Typography>
          <br />
          <div className="Div-relative-container  ">
            <CssTextField
              variant="outlined"
              autoFocus={true}
              fullWidth={true}
              type={'text'}
              value={amount}
              onChange={handleSetAmount.bind(this)}
              label="Kong to Burn for Bananas"
              id="kong-amount"
            />
            <Button
              variant={'outlined'}
              color={'success'}
              className="Input-max-button"
              onClick={maxKong}
            >
              max
            </Button>
          </div>
          <br />
          <br />
          <Button
            variant={'contained'}
            color={'warning'}
            onClick={changeArbDirection}
            className="Arb-change-direction-button"
          >
            Change Burn Direction
          </Button>
          <br />
          <br />
          <ArbKongEstimate amount={amount} />
          <br />
          <br />
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={10}>
                <Typography
                  id="modal-modal-title"
                  variant="caption"
                  component="h2"
                  align={'left'}
                  color={'white'}
                >
                  Slippage Tolerance
                  <Tooltip
                    title="The maximum difference between your estimated price and execution price."
                    placement="right"
                  >
                    <HelpOutlineIcon
                      style={{
                        position: 'relative',
                        marginLeft: '2px',
                        top: '5px',
                      }}
                      fontSize="small"
                      color={'primary'}
                    />
                  </Tooltip>
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography
                  id="modal-modal-title"
                  variant="caption"
                  component="h2"
                  align={'right'}
                  color={'white'}
                  style={{ position: 'relative', top: '5px' }}
                >
                  0.5%
                </Typography>
              </Grid>
            </Grid>
          </Box>
          <br />
          {loading ? (
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={7}>
                  <CircularProgress color="success" />
                </Grid>
                <Grid item xs={5}>
                  {arbTransactionSignature && (
                    <Link
                      className={styles.link}
                      href={`https://explorer.solana.com/tx/${arbTransactionSignature}?cluster=${WALLET_ADAPTER_NETWORK}`}
                      target="_blank"
                      align="right"
                    >
                      Transaction
                      <LaunchIcon className={styles.icon} />
                    </Link>
                  )}
                </Grid>
              </Grid>
            </Box>
          ) : (
            <>
              <Button onClick={arbitrageKong} variant={'contained'}>
                Burn Kong for Bananas
              </Button>
            </>
          )}
          <br />
          <br />
        </div>
      </div>
    )
  }
}
